// pages/AdminPage.jsx
import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '../context/ProductContext';
import {
    Container, Typography, Box, Button, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    Alert, Chip, Switch, FormControlLabel, Grid, CircularProgress,
    Tooltip, LinearProgress
} from '@mui/material';
import { Edit, Delete, Add, Refresh, Lock, Logout, CloudUpload, Error as ErrorIcon, CheckCircle } from '@mui/icons-material';

// ✅ Memoized Product Row component
const ProductRow = memo(({ product, onEdit, onDelete, getImageUrl }) => {
    const [imageError, setImageError] = useState(false);
    const [imageUrl, setImageUrl] = useState('');

    useEffect(() => {
        const url = getImageUrl(product.image);
        setImageUrl(url);
        setImageError(false);
    }, [product, getImageUrl]);

    const handleImageError = useCallback((e) => {
        console.error(`❌ Image failed to load for ${product.title}:`, product.image);
        setImageError(true);
        // Use a fallback with product initial
        const fallbackUrl = `https://via.placeholder.com/50x50/667eea/ffffff?text=${encodeURIComponent(product.title.charAt(0))}`;
        e.target.src = fallbackUrl;
    }, [product]);

    return (
        <TableRow hover>
            <TableCell>{product.id}</TableCell>
            <TableCell>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box
                        component="img"
                        src={imageUrl}
                        alt={product.title}
                        sx={{
                            width: 50,
                            height: 50,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            backgroundColor: '#f5f5f5',
                        }}
                        onError={handleImageError}
                        onLoad={() => {
                            console.log(`✅ Image loaded for ${product.title}`);
                            setImageError(false);
                        }}
                        loading="lazy"
                    />
                    {imageError && (
                        <Tooltip title={`Image not found: ${product.image}`}>
                            <ErrorIcon 
                                sx={{ 
                                    position: 'absolute', 
                                    top: -4, 
                                    right: -4, 
                                    color: 'error.main',
                                    fontSize: 16,
                                    backgroundColor: 'white',
                                    borderRadius: '50%'
                                }} 
                            />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
            <TableCell sx={{ maxWidth: 150 }}>
                <Typography variant="body2" noWrap>
                    {product.title}
                </Typography>
            </TableCell>
            <TableCell>
                <Chip label={product.category || 'Uncategorized'} size="small" />
            </TableCell>
            <TableCell>
                {product.price !== null && product.price !== undefined ? (
                    `KES ${product.price.toLocaleString()}`
                ) : (
                    <Chip label="Contact" size="small" color="warning" />
                )}
            </TableCell>
            <TableCell>
                {product.stock > 0 ? (
                    <Chip label={`${product.stock} in stock`} size="small" color="success" />
                ) : (
                    <Chip label="Out of stock" size="small" color="error" />
                )}
            </TableCell>
            <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {product.is_new && <Chip label="NEW" size="small" color="primary" />}
                    {product.is_bestseller && <Chip label="⭐ BEST" size="small" sx={{ bgcolor: '#FFD700' }} />}
                    {product.is_on_sale && <Chip label="SALE" size="small" color="error" />}
                </Box>
            </TableCell>
            <TableCell align="center">
                <IconButton 
                    onClick={() => onEdit(product)} 
                    color="primary" 
                    size="small" 
                    title="Edit"
                >
                    <Edit />
                </IconButton>
                <IconButton 
                    onClick={() => onDelete(product.id)} 
                    color="error" 
                    size="small" 
                    title="Delete"
                >
                    <Delete />
                </IconButton>
            </TableCell>
        </TableRow>
    );
});

// ✅ Memoized Product Table
const ProductTable = memo(({ products, onEdit, onDelete, getImageUrl }) => {
    if (products.length === 0) {
        return (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    No products found. Click "Add Product" to create your first product.
                </Typography>
            </Paper>
        );
    }

    return (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Image</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Stock</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {products.map((product) => (
                        <ProductRow
                            key={product.id}
                            product={product}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            getImageUrl={getImageUrl}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
});

// ✅ Main AdminPage Component
function AdminPage() {
    const navigate = useNavigate();
    const { products, addProduct, updateProduct, deleteProduct, refreshProducts, loading } = useProducts();
    
    const [open, setOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [imagePreviewError, setImagePreviewError] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        short_description: '',
        price: '',
        discount_price: '',
        image: '',
        category: '',
        stock: '',
        brand: '',
        is_new: false,
        is_bestseller: false,
        is_on_sale: false,
        discount_percentage: '',
    });

    const ADMIN_PASSWORD = 'admin123';
// frontend/src/pages/AdminPage.jsx
// Update the API_URL for production

// ❌ Remove this for production
// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ✅ Add this for production
//const API_URL = 'https://uswellness.co.ke/api';
//const API_URL = process.env.REACT_APP_API_URL || 'https://uswellness.co.ke/api';
//const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_URL = process.env.NODE_ENV === 'production' 
    ? 'https://uswellness.co.ke/api'
    : 'http://localhost:5000/api';

    // ✅ Enhanced getImageUrl
    const getImageUrl = useCallback((imagePath) => {
        if (!imagePath) {
            return '/placeholder-image.jpg';
        }
        
        if (imagePath.startsWith('http')) {
            return imagePath;
        }
        
        if (imagePath.startsWith('/static')) {
            return imagePath;
        }
        
        // For other paths, construct URL
        return `/static/images/products/${imagePath}`;
    }, []);

    useEffect(() => {
        const auth = sessionStorage.getItem('adminAuth');
        if (auth === 'true') {
            setIsAuthenticated(true);
            refreshProducts();
        }
    }, [refreshProducts]);

    const handleLogin = useCallback(() => {
        if (password === ADMIN_PASSWORD) {
            sessionStorage.setItem('adminAuth', 'true');
            setIsAuthenticated(true);
            setLoginError('');
            refreshProducts();
        } else {
            setLoginError('Incorrect password. Please try again.');
            setPassword('');
        }
    }, [password, refreshProducts]);

    const handleLogout = useCallback(() => {
        sessionStorage.removeItem('adminAuth');
        setIsAuthenticated(false);
        navigate('/');
    }, [navigate]);

    // ✅ Fixed handleImageUpload
    const handleImageUpload = useCallback(async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        console.log(`📤 Uploading file: ${file.name}, size: ${file.size} bytes`);

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Image size should be less than 5MB.');
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setUploadSuccess(false);
        setImagePreviewError(false);

        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await fetch(`${API_URL}/upload`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Upload failed: ${response.status} - ${errorText}`);
            }

            // ✅ Directly parse JSON - no getReader() interference
            const data = await response.json();
            
            if (!data.imagePath) {
                throw new Error('Server did not return image path');
            }

            console.log('✅ Upload successful:', data);
            
            setFormData(prev => ({
                ...prev,
                image: data.imagePath
            }));
            
            setUploadSuccess(true);
            setUploadProgress(100);
            
            // Show success message
            setTimeout(() => {
                setUploadSuccess(false);
            }, 3000);
            
            alert(`✅ Image uploaded successfully!\nPath: ${data.imagePath}`);

        } catch (error) {
            console.error('❌ Upload error:', error);
            alert(`⚠️ Failed to upload image: ${error.message}`);
        } finally {
            setUploading(false);
            setUploadProgress(0);
            event.target.value = '';
        }
    }, [API_URL]);

    const handleOpen = useCallback((product = null) => {
        if (product) {
            setEditingProduct(product);
            setFormData({
                title: product.title || '',
                description: product.description || '',
                short_description: product.short_description || '',
                price: product.price !== null && product.price !== undefined ? product.price : '',
                discount_price: product.discount_price !== null && product.discount_price !== undefined ? product.discount_price : '',
                image: product.image || '',
                category: product.category || '',
                stock: product.stock !== null && product.stock !== undefined ? product.stock : '',
                brand: product.brand || '',
                is_new: product.is_new || false,
                is_bestseller: product.is_bestseller || false,
                is_on_sale: product.is_on_sale || false,
                discount_percentage: product.discount_percentage || '',
            });
        } else {
            setEditingProduct(null);
            setFormData({
                title: '',
                description: '',
                short_description: '',
                price: '',
                discount_price: '',
                image: '',
                category: '',
                stock: '',
                brand: '',
                is_new: false,
                is_bestseller: false,
                is_on_sale: false,
                discount_percentage: '',
            });
        }
        setImagePreviewError(false);
        setUploadSuccess(false);
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        setEditingProduct(null);
        setImagePreviewError(false);
        setUploadSuccess(false);
        setUploading(false);
    }, []);

    const handleSubmit = useCallback(() => {
        if (!formData.title.trim()) {
            alert('Product title is required.');
            return;
        }

        // Check if image was uploaded (for new products)
        if (!editingProduct && !formData.image) {
            alert('Please upload an image for the product.');
            return;
        }

        const productData = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            short_description: formData.short_description.trim() || formData.description?.substring(0, 100) || '',
            price: formData.price !== '' ? parseFloat(formData.price) : null,
            discount_price: formData.discount_price !== '' ? parseFloat(formData.discount_price) : null,
            image: formData.image || '/placeholder-image.jpg',
            category: formData.category.trim() || 'Uncategorized',
            stock: formData.stock !== '' ? parseInt(formData.stock) : 0,
            brand: formData.brand.trim() || '',
            is_new: formData.is_new,
            is_bestseller: formData.is_bestseller,
            is_on_sale: formData.is_on_sale,
            discount_percentage: formData.discount_percentage !== '' ? parseInt(formData.discount_percentage) : 0,
            rating: editingProduct?.rating || 4.0,
            reviews: editingProduct?.reviews || 0,
        };

        console.log('📦 Saving product:', productData);

        if (editingProduct) {
            updateProduct(editingProduct.id, productData);
        } else {
            addProduct(productData);
        }
        handleClose();
    }, [formData, editingProduct, updateProduct, addProduct, handleClose]);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }, []);

    // ✅ Memoized login screen
    const LoginScreen = useMemo(() => (
        <Container maxWidth="sm" sx={{ py: 12 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
                <Box
                    sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: '#667eea20',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                        mb: 2
                    }}
                >
                    <Lock sx={{ fontSize: 32, color: '#667eea' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Admin Access Required
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Please enter the admin password to continue
                </Typography>

                {loginError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {loginError}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    type="password"
                    label="Admin Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    sx={{ mb: 2 }}
                />
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleLogin}
                    size="large"
                    sx={{
                        bgcolor: '#667eea',
                        '&:hover': { bgcolor: '#5a6fd6' },
                        py: 1.5
                    }}
                >
                    Login
                </Button>
                <Button
                    fullWidth
                    variant="text"
                    onClick={() => navigate('/')}
                    sx={{ mt: 1, color: 'text.secondary' }}
                >
                    ← Back to Home
                </Button>
            </Paper>
        </Container>
    ), [password, loginError, handleLogin, navigate]);

    if (!isAuthenticated) {
        return LoginScreen;
    }

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading products...</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    🛒 Product Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={refreshProducts}
                        size="small"
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpen()}
                        sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#5a6fd6' } }}
                    >
                        Add Product
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Logout />}
                        onClick={handleLogout}
                        size="small"
                    >
                        Logout
                    </Button>
                </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                Total Products: <strong>{products.length}</strong>
            </Alert>

            <ProductTable
                products={products}
                onEdit={handleOpen}
                onDelete={deleteProduct}
                getImageUrl={getImageUrl}
            />

            {/* Add/Edit Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingProduct ? '✏️ Edit Product' : '➕ Add New Product'}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Product Title *"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                multiline
                                rows={3}
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Short Description"
                                name="short_description"
                                value={formData.short_description}
                                onChange={handleChange}
                                helperText="Brief description shown on product cards"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Price (KES)"
                                name="price"
                                type="number"
                                value={formData.price}
                                onChange={handleChange}
                                placeholder="Leave empty for 'Contact for Price'"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Discount Price (KES)"
                                name="discount_price"
                                type="number"
                                value={formData.discount_price}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Category"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                placeholder="e.g. Supplements, Vitamins"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Brand"
                                name="brand"
                                value={formData.brand}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Stock Quantity"
                                name="stock"
                                type="number"
                                value={formData.stock}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Discount Percentage"
                                name="discount_percentage"
                                type="number"
                                value={formData.discount_percentage}
                                onChange={handleChange}
                                placeholder="e.g. 20"
                            />
                        </Grid>

                        {/* Image Upload Section */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Product Image
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                {formData.image && (
                                    <Box
                                        component="img"
                                        src={getImageUrl(formData.image)}
                                        alt="Preview"
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            objectFit: 'cover',
                                            borderRadius: 2,
                                            border: '1px solid #e0e0e0'
                                        }}
                                        onError={(e) => {
                                            console.error('❌ Preview error:', formData.image);
                                            e.target.src = '/placeholder-image.jpg';
                                            setImagePreviewError(true);
                                        }}
                                        onLoad={() => {
                                            console.log('✅ Preview loaded:', formData.image);
                                            setImagePreviewError(false);
                                        }}
                                        loading="lazy"
                                    />
                                )}
                                
                                <Box sx={{ flex: 1 }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<CloudUpload />}
                                        disabled={uploading}
                                        sx={{ minWidth: 200 }}
                                    >
                                        {uploading ? 'Uploading...' : '📤 Choose Image'}
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                        />
                                    </Button>
                                    
                                    {uploading && (
                                        <Box sx={{ mt: 1 }}>
                                            <LinearProgress variant="determinate" value={uploadProgress} />
                                            <Typography variant="caption" color="text.secondary">
                                                Uploading... {uploadProgress}%
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {uploadSuccess && (
                                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                                            <Typography variant="caption" color="success.main">
                                                Upload complete!
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <TextField
                                    fullWidth
                                    label="Image URL (or upload above)"
                                    name="image"
                                    value={formData.image}
                                    onChange={handleChange}
                                    placeholder="/static/images/products/your-image.jpg"
                                    size="small"
                                    sx={{ mt: 1, flex: 1, minWidth: 200 }}
                                    helperText="Example: /static/images/products/filename.jpg"
                                />
                            </Box>
                            {imagePreviewError && (
                                <Typography variant="caption" color="warning" sx={{ display: 'block', mt: 1 }}>
                                    ⚠️ Image file not found at this path. Please upload a new image or correct the path.
                                </Typography>
                            )}
                            {formData.image && !imagePreviewError && (
                                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                    ✅ Image path: {formData.image}
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="is_new"
                                            checked={formData.is_new}
                                            onChange={handleChange}
                                            color="primary"
                                        />
                                    }
                                    label="New Product"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="is_bestseller"
                                            checked={formData.is_bestseller}
                                            onChange={handleChange}
                                            color="secondary"
                                        />
                                    }
                                    label="Bestseller"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="is_on_sale"
                                            checked={formData.is_on_sale}
                                            onChange={handleChange}
                                            color="error"
                                        />
                                    }
                                    label="On Sale"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        sx={{
                            bgcolor: '#667eea',
                            '&:hover': { bgcolor: '#5a6fd6' },
                        }}
                        disabled={uploading}
                    >
                        {editingProduct ? 'Update Product' : 'Add Product'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default memo(AdminPage);